import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Link, CheckCircle, XCircle, Search, Bot, Users } from "lucide-react";

interface LinkSuggestion {
  mcq_id: string;
  note_id: string;
  relevance_score: number;
  confidence_score: number;
  link_type: string;
  reason: string;
}

interface MCQNoteLink {
  id: string;
  mcq_id: string;
  note_id: string;
  relevance_score: number;
  created_at: string;
  mcqs: {
    id: string;
    stem: string;
    tags: string[] | null;
  };
  notes: {
    id: string;
    title: string;
    content: string;
  };
}

const fetchExistingLinks = async (): Promise<MCQNoteLink[]> => {
  const { data, error } = await supabase
    .from('mcq_note_links')
    .select(`
      *,
      mcqs(id, stem, tags),
      notes(id, title, content)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

const fetchMCQs = async () => {
  const { data, error } = await supabase
    .from('mcqs')
    .select('id, stem, tags')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

const fetchNotes = async () => {
  const { data, error } = await supabase
    .from('notes')
    .select('id, title')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const MCQNotesLinkManager = () => {
  const [activeTab, setActiveTab] = useState("suggestions");
  const [selectedMCQ, setSelectedMCQ] = useState<string>("");
  const [selectedNote, setSelectedNote] = useState<string>("");
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [suggestions, setSuggestions] = useState<LinkSuggestion[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const queryClient = useQueryClient();

  const { data: existingLinks, isLoading: linksLoading } = useQuery({
    queryKey: ["mcq-note-links"],
    queryFn: fetchExistingLinks,
  });

  const { data: mcqs } = useQuery({
    queryKey: ["mcqs"],
    queryFn: fetchMCQs,
  });

  const { data: notes } = useQuery({
    queryKey: ["notes"],
    queryFn: fetchNotes,
  });

  const createLinkMutation = useMutation({
    mutationFn: async (link: { mcq_id: string; note_id: string; relevance_score: number }) => {
      const { data, error } = await supabase
        .from('mcq_note_links')
        .insert({
          mcq_id: link.mcq_id,
          note_id: link.note_id,
          relevance_score: link.relevance_score
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-note-links"] });
      toast.success("Link created successfully");
    },
    onError: (error) => {
      toast.error(`Error creating link: ${error.message}`);
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      const { error } = await supabase
        .from('mcq_note_links')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-note-links"] });
      toast.success("Link deleted successfully");
    },
    onError: (error) => {
      toast.error(`Error deleting link: ${error.message}`);
    },
  });

  const approveLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      // Note: approved_by and approved_at fields don't exist in current schema
      // This functionality needs to be added to the database schema first
      const { error } = await supabase
        .from('mcq_note_links')
        .update({ relevance_score: 10 }) // Mark as manually approved with max relevance
        .eq('id', linkId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mcq-note-links"] });
      toast.success("Link approved successfully");
    },
  });

  const generateSuggestions = async (batchProcess = false) => {
    setIsAnalyzing(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-content-linker', {
        body: {
          mcq_id: selectedMCQ === "all" ? undefined : selectedMCQ || undefined,
          note_id: selectedNote === "all" ? undefined : selectedNote || undefined,
          batch_process: batchProcess,
          confidence_threshold: confidenceThreshold,
        },
      });

      if (error) throw error;

      setSuggestions(data.suggestions || []);
      toast.success(`Found ${data.suggestions?.length || 0} AI suggestions`);
      setActiveTab("suggestions");
    } catch (error) {
      toast.error(`Error generating suggestions: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const createLinkFromSuggestion = (suggestion: LinkSuggestion) => {
    createLinkMutation.mutate({
      mcq_id: suggestion.mcq_id,
      note_id: suggestion.note_id,
      relevance_score: suggestion.relevance_score,
    });
  };


  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">MCQ-Notes Link Manager</h2>
        <p className="text-muted-foreground">
          Use AI to automatically suggest and manage relationships between MCQs and revision notes.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generate">Generate Links</TabsTrigger>
          <TabsTrigger value="suggestions">AI Suggestions</TabsTrigger>
          <TabsTrigger value="existing">Existing Links</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Link Generation
              </CardTitle>
              <CardDescription>
                Generate AI-powered suggestions for linking MCQs to relevant revision notes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Select MCQ (Optional)</label>
                  <Select value={selectedMCQ} onValueChange={setSelectedMCQ}>
                    <SelectTrigger>
                      <SelectValue placeholder="All MCQs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All MCQs</SelectItem>
                      {mcqs?.map((mcq) => (
                        <SelectItem key={mcq.id} value={mcq.id}>
                          {mcq.stem.substring(0, 50)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Select Note (Optional)</label>
                  <Select value={selectedNote} onValueChange={setSelectedNote}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Notes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Notes</SelectItem>
                      {notes?.map((note) => (
                        <SelectItem key={note.id} value={note.id}>
                          {note.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Confidence Threshold: {confidenceThreshold}
                </label>
                <Input
                  type="range"
                  min="0.5"
                  max="1.0"
                  step="0.1"
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => generateSuggestions(false)}
                  disabled={isAnalyzing}
                  className="flex-1"
                >
                  {isAnalyzing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Search className="mr-2 h-4 w-4" />
                  Generate Targeted Suggestions
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={isAnalyzing}>
                      <Bot className="mr-2 h-4 w-4" />
                      Batch Process All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Batch Process All Content</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will analyze all MCQs and notes to find potential links. This may take several minutes and use significant AI resources.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => generateSuggestions(true)}>
                        Start Batch Processing
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">AI Link Suggestions</h3>
            <Badge variant="secondary">{suggestions.length} suggestions</Badge>
          </div>

          {suggestions.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Bot className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No suggestions yet. Generate AI suggestions to see potential links.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            {suggestion.link_type}
                          </Badge>
                          <Badge variant="outline">
                            Relevance: {(suggestion.relevance_score * 100).toFixed(0)}%
                          </Badge>
                          <Badge variant="outline">
                            Confidence: {(suggestion.confidence_score * 100).toFixed(0)}%
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{suggestion.reason}</p>
                        <div className="text-xs text-muted-foreground">
                          MCQ: {mcqs?.find(m => m.id === suggestion.mcq_id)?.stem.substring(0, 100)}...
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Note: {notes?.find(n => n.id === suggestion.note_id)?.title}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => createLinkFromSuggestion(suggestion)}
                          disabled={createLinkMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSuggestions(prev => prev.filter((_, i) => i !== index))}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="existing" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Existing Links</h3>
            <Badge variant="secondary">{existingLinks?.length || 0} links</Badge>
          </div>

          {linksLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : existingLinks?.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <Link className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  No existing links found. Create some AI suggestions to get started.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {existingLinks?.map((link) => (
                <Card key={link.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">
                            Link
                          </Badge>
                          <Badge variant="outline">
                            Relevance: {link.relevance_score}/10
                          </Badge>
                        </div>
                        <div className="text-sm">
                          <strong>MCQ:</strong> {link.mcqs.stem.substring(0, 100)}...
                        </div>
                        <div className="text-sm">
                          <strong>Note:</strong> {link.notes.title}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive">
                              <XCircle className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Link</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this link? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteLinkMutation.mutate(link.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};